import { useGT } from 'gt-react';
import type { SD849Status } from '@erp/rpc';

/**
 * The user-visible vocabulary of the 849 claim Status, shared by the detail
 * page's picker and the responses list's column + filter.
 *
 * One definition on purpose: a status the list spells differently from the page
 * ops set it on reads as a different status.
 */

/**
 * The six values, in the order the picker and the filter offer them.
 *
 * There is deliberately no `none` entry here — see the note in
 * `ClaimStatusPicker` on why "No status" is not something a user can choose.
 * The filter is the one place it belongs, and it appends it itself.
 */
export const CLAIM_STATUS_ORDER: SD849Status[] = [
  'needs_review',
  'waiting_on_supplier',
  'resubmitted',
  'needs_more_data',
  'complete',
  'closed',
];

export interface ClaimStatusLabel {
  label: string;
  description: string;
}

/**
 * Labels and descriptions for the six values.
 *
 * A hook, not a module constant, because these are user-visible strings and
 * have to go through `gt` at render time — a constant would freeze whatever
 * locale happened to be active when the module loaded.
 */
export function useClaimStatusLabels(): Record<SD849Status, ClaimStatusLabel> {
  const gt = useGT();
  const context = 'ship-and-debit 849 claim status';
  return {
    needs_review: {
      label: gt('Needs review', { context }),
      description: gt(
        'Nobody has triaged this yet, or new information arrived',
        { context },
      ),
    },
    waiting_on_supplier: {
      label: gt('Waiting on supplier', { context }),
      description: gt('We’ve asked; the ball is with them', { context }),
    },
    resubmitted: {
      label: gt('Resubmitted', { context }),
      description: gt('We sent a resubmission and are waiting for a new 849', {
        context,
      }),
    },
    needs_more_data: {
      label: gt('Needs more data', { context }),
      description: gt('Blocked on us — we’re missing information to act', {
        context,
      }),
    },
    complete: {
      label: gt('Complete', { context }),
      description: gt(
        'The supplier credited the claim in full. Nothing outstanding',
        { context },
      ),
    },
    closed: {
      label: gt('Closed', { context }),
      description: gt('We’re no longer pursuing this, without full credit', {
        context,
      }),
    },
  };
}

/** How an un-triaged 849 reads — the same words everywhere it appears. */
export function useNoClaimStatusLabel(): string {
  const gt = useGT();
  return gt('No status', {
    context: 'ship-and-debit 849 claim status, none set',
  });
}
